import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import portfolioService from '../services/portfolio';
import { getTopXAndOthers, CHART_COLORS, useIsMobile } from '../utils/chartHelpers';
import ChartContainer from '../components/ui/ChartContainer';

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #f4f4f5',
      borderRadius: '8px',
      padding: '8px 12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      fontSize: '0.8rem',
    }}>
      <div style={{ fontWeight: 600, color: '#18181b', marginBottom: '2px' }}>{payload[0].name}</div>
      <div style={{ color: '#71717a' }}>{((payload[0].value as number) * 100).toFixed(2)}%</div>
    </div>
  );
};

const ETFDetail = () => {
  const { id } = useParams<{ id: string }>();
  const etfId = parseInt(id || '0', 10);
  const isMobile = useIsMobile();

  const { data: etf, isLoading: etfLoading, error: etfError } = useQuery({
    queryKey: ['etf', etfId],
    queryFn: () => portfolioService.getETF(etfId),
    enabled: !!etfId,
  });

  const { data: holdings, isLoading: holdingsLoading, error: holdingsError } = useQuery({
    queryKey: ['etf-holdings', etfId],
    queryFn: () => portfolioService.getETFHoldings(etfId),
    enabled: !!etfId,
  });

  const safeHoldings = holdings || [];

  const industryData = safeHoldings.length > 0
    ? Object.entries(
        safeHoldings.reduce((acc, h) => {
          if (h?.industry) {
            const w = typeof h.weight === 'number' ? h.weight : parseFloat(h.weight) || 0;
            acc[h.industry] = (acc[h.industry] || 0) + w;
          }
          return acc;
        }, {} as Record<string, number>)
      ).map(([name, value]) => ({ name, value }))
    : [];

  const countryData = safeHoldings.length > 0
    ? Object.entries(
        safeHoldings.reduce((acc, h) => {
          if (h?.country) {
            const w = typeof h.weight === 'number' ? h.weight : parseFloat(h.weight) || 0;
            acc[h.country] = (acc[h.country] || 0) + w;
          }
          return acc;
        }, {} as Record<string, number>)
      ).map(([name, value]) => ({ name, value }))
    : [];

  const topIndustry = getTopXAndOthers(industryData, 6);
  const topCountry = getTopXAndOthers(countryData, 6);

  const legendConfig = isMobile ? null : {
    layout: 'horizontal' as const,
    wrapperStyle: { paddingTop: '12px', fontSize: '12px', color: '#71717a' },
  };

  const legendFormatter = (value: string, entry: any) => {
    const pct = (entry.payload.value * 100).toFixed(1);
    return <span style={{ color: '#52525b', fontSize: '12px' }}>{value} ({pct}%)</span>;
  };

  if (etfLoading || holdingsLoading) {
    return <div className="text-center p-4" style={{ color: '#71717a' }}>Loading ETF details...</div>;
  }
  if (etfError || holdingsError || !etf) {
    return <div className="text-center p-4 text-danger">Error loading ETF data</div>;
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="mb-4 pb-4" style={{ borderBottom: '1px solid #f4f4f5' }}>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end gap-2">
          <div>
            <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginBottom: '4px' }}>
              <Link to="/etfs" style={{ color: '#a1a1aa', textDecoration: 'none' }}>ETFs</Link>
              {' / '}
              <span>{etf.ticker}</span>
            </div>
            <h1 className="fw-semibold mb-1" style={{ color: '#000537', letterSpacing: '-0.025em' }}>
              {etf.ticker}
            </h1>
            <div style={{ fontSize: '0.875rem', color: '#71717a' }}>{etf.name}</div>
          </div>
          <div className="text-md-end">
            <div style={{ fontSize: '0.8rem', color: '#71717a' }}>
              {etf.provider?.name}
              <span style={{ color: '#d4d4d8', margin: '0 6px' }}>·</span>
              Updated {etf.last_updated_at ? new Date(etf.last_updated_at).toLocaleDateString() : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="card card-forest h-100">
            <div className="card-body">
              <div className="section-label mb-3">Industry Breakdown</div>
              {industryData.length > 0 ? (
                <ChartContainer size="md">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={topIndustry}
                        cx="50%" cy="50%"
                        innerRadius="40%"
                        outerRadius="68%"
                        paddingAngle={2}
                        dataKey="value"
                        labelLine={false}
                      >
                        {topIndustry.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} stroke="none" />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      {legendConfig && <Legend formatter={legendFormatter} {...legendConfig} />}
                    </PieChart>
                  </ResponsiveContainer>
                </ChartContainer>
              ) : (
                <div className="d-flex align-items-center justify-content-center py-5" style={{ color: '#a1a1aa', fontSize: '0.875rem' }}>
                  No industry data available
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card card-forest h-100">
            <div className="card-body">
              <div className="section-label mb-3">Country Breakdown</div>
              {countryData.length > 0 ? (
                <ChartContainer size="md">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={topCountry}
                        cx="50%" cy="50%"
                        innerRadius="40%"
                        outerRadius="68%"
                        paddingAngle={2}
                        dataKey="value"
                        labelLine={false}
                      >
                        {topCountry.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} stroke="none" />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      {legendConfig && <Legend formatter={legendFormatter} {...legendConfig} />}
                    </PieChart>
                  </ResponsiveContainer>
                </ChartContainer>
              ) : (
                <div className="d-flex align-items-center justify-content-center py-5" style={{ color: '#a1a1aa', fontSize: '0.875rem' }}>
                  No country data available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Holdings table */}
      <div className="card" style={{ border: '1px solid #f4f4f5', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f4f4f5' }}>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <span className="fw-semibold" style={{ color: '#000537', fontSize: '0.95rem' }}>Holdings</span>
              {safeHoldings.length > 0 && (
                <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#a1a1aa' }}>
                  {safeHoldings.length} positions
                </span>
              )}
            </div>
            {etf.provider?.name === 'Xtrackers' && (
              <span style={{
                fontSize: '0.72rem',
                color: '#92400e',
                backgroundColor: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '6px',
                padding: '3px 8px',
              }}>
                Top 10 holdings only — full breakdown not available from this provider
              </span>
            )}
          </div>
        </div>

        {safeHoldings.length > 0 ? (
          <div style={{ overflowX: 'auto' }}><div style={{ minWidth: 560 }}>
            {/* Header row */}
            <div style={{
              padding: '10px 20px',
              display: 'grid',
              gridTemplateColumns: '1fr 80px 90px 160px 120px',
              gap: '12px',
              borderBottom: '1px solid #f4f4f5',
            }}>
              <span className="section-label">Asset</span>
              <span className="section-label">Type</span>
              <span className="section-label">Weight</span>
              <span className="section-label">Industry</span>
              <span className="section-label">Country</span>
            </div>

            {safeHoldings.map((holding, idx) => (
              <div
                key={holding.id}
                style={{
                  padding: '11px 20px',
                  display: 'grid',
                  gridTemplateColumns: '1fr 80px 90px 160px 120px',
                  gap: '12px',
                  alignItems: 'center',
                  borderBottom: idx < safeHoldings.length - 1 ? '1px solid #f4f4f5' : 'none',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#fafafa')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#18181b', fontFamily: 'monospace' }}>
                    {holding.asset_identifier}
                  </div>
                  {holding.asset_name && (
                    <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginTop: '1px' }}>
                      {holding.asset_name}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#71717a' }}>
                  {holding.asset_type || <span style={{ color: '#d4d4d8' }}>—</span>}
                </div>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#000537', fontVariantNumeric: 'tabular-nums' }}>
                  {typeof holding.weight === 'number'
                    ? (holding.weight * 100).toFixed(2)
                    : (parseFloat(holding.weight) * 100 || 0).toFixed(2)}%
                </div>
                <div style={{ fontSize: '0.8rem', color: '#52525b' }}>
                  {holding.industry || <span style={{ color: '#d4d4d8' }}>—</span>}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#52525b' }}>
                  {holding.country || <span style={{ color: '#d4d4d8' }}>—</span>}
                </div>
              </div>
            ))}
          </div></div>
        ) : (
          <div className="text-center py-5" style={{ color: '#a1a1aa' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="currentColor" className="mb-3" viewBox="0 0 16 16" style={{ color: '#d4d4d8', display: 'block', margin: '0 auto 12px' }}>
              <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm15 2h-4v3h4V4zm0 4h-4v3h4V8zm0 4h-4v3h3a1 1 0 0 0 1-1v-2zm-5 3v-3H6v3h4zm-5 0v-3H1v2a1 1 0 0 0 1 1h3zm-4-4h4V8H1v3zm0-4h4V4H1v3zm5-3v3h4V4H6zm4 4H6v3h4V8z"/>
            </svg>
            <div style={{ fontSize: '0.875rem' }}>No holdings data available for this ETF</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ETFDetail;
